import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  IconButton,
  Chip,
  Stack,
  Box,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  PushPin as PinIcon,
  PushPinOutlined as PinOutlinedIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreIcon,
  TaskAlt as TaskIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import type { NoteWithRelations } from '../../types/note';
import { NOTE_TYPE_OPTIONS } from '../../types/note';

interface NoteCardProps {
  note: NoteWithRelations;
  onEdit: (note: NoteWithRelations) => void;
  onDelete: (note: NoteWithRelations) => void;
  onPin: (note: NoteWithRelations) => void;
  onArchive: (note: NoteWithRelations) => void;
  onConvertToTask: (note: NoteWithRelations) => void;
}

export const NoteCard: React.FC<NoteCardProps> = ({
  note,
  onEdit,
  onDelete,
  onPin,
  onArchive,
  onConvertToTask,
}) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleAction = (action: () => void) => {
    handleMenuClose();
    action();
  };

  const typeOption = NOTE_TYPE_OPTIONS.find((t) => t.value === note.type);
  const clientName = note.clientId
    ? `${note.clientId.firstName} ${note.clientId.lastName}`
    : null;

  const truncateContent = (content: string, maxLength: number = 150) => {
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength).trim() + '...';
  };

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        borderTop: note.color ? `4px solid ${note.color}` : undefined,
        opacity: note.isArchived ? 0.7 : 1,
        '&:hover': {
          boxShadow: 4,
          transform: 'translateY(-2px)',
        },
      }}
      onClick={() => onEdit(note)}
    >
      <CardContent sx={{ flex: 1, pb: 1 }}>
        {/* Header with type and pin */}
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1}>
          <Chip
            label={`${typeOption?.icon || ''} ${typeOption?.label || note.type}`}
            size="small"
            variant="outlined"
            sx={{ fontSize: '0.7rem' }}
          />
          <Stack direction="row" spacing={0.5}>
            {note.isPinned && (
              <Tooltip title="Pinned">
                <PinIcon fontSize="small" color="primary" />
              </Tooltip>
            )}
            <IconButton
              size="small"
              onClick={handleMenuOpen}
              sx={{ ml: 'auto' }}
            >
              <MoreIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>

        {/* Title */}
        <Typography
          variant="subtitle1"
          fontWeight={600}
          gutterBottom
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {note.title}
        </Typography>

        {/* Content preview */}
        {note.content && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              mb: 1,
            }}
          >
            {truncateContent(note.content)}
          </Typography>
        )}

        {/* Client badge */}
        {clientName && (
          <Chip
            icon={<PersonIcon />}
            label={clientName}
            size="small"
            variant="outlined"
            sx={{ mb: 1 }}
          />
        )}

        {/* Tags */}
        {note.tags.length > 0 && (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
            {note.tags.slice(0, 3).map((tag) => (
              <Chip
                key={tag}
                label={tag}
                size="small"
                variant="outlined"
                sx={{ fontSize: '0.65rem', height: 20 }}
              />
            ))}
            {note.tags.length > 3 && (
              <Chip
                label={`+${note.tags.length - 3}`}
                size="small"
                sx={{ fontSize: '0.65rem', height: 20 }}
              />
            )}
          </Stack>
        )}

        {/* Converted indicator */}
        {note.convertedToTaskId && (
          <Chip
            icon={<TaskIcon />}
            label="Converted to task"
            size="small"
            color="success"
            variant="outlined"
            sx={{ mt: 1 }}
          />
        )}
      </CardContent>

      {/* Footer */}
      <Box px={2} pb={1}>
        <Typography variant="caption" color="text.secondary">
          {formatDistanceToNow(new Date(note.updatedAt), { addSuffix: true })}
        </Typography>
      </Box>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        onClick={(e) => e.stopPropagation()}
      >
        <MenuItem onClick={() => handleAction(() => onEdit(note))}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleAction(() => onPin(note))}>
          <ListItemIcon>
            {note.isPinned ? <PinOutlinedIcon fontSize="small" /> : <PinIcon fontSize="small" />}
          </ListItemIcon>
          <ListItemText>{note.isPinned ? 'Unpin' : 'Pin'}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleAction(() => onArchive(note))}>
          <ListItemIcon>
            {note.isArchived ? <UnarchiveIcon fontSize="small" /> : <ArchiveIcon fontSize="small" />}
          </ListItemIcon>
          <ListItemText>{note.isArchived ? 'Restore' : 'Archive'}</ListItemText>
        </MenuItem>
        {!note.convertedToTaskId && (
          <MenuItem onClick={() => handleAction(() => onConvertToTask(note))}>
            <ListItemIcon><TaskIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Convert to Task</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={() => handleAction(() => onDelete(note))} sx={{ color: 'error.main' }}>
          <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>
    </Card>
  );
};

export default NoteCard;
